
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, User, Calendar } from 'lucide-react';

const PatientNotes = () => {
  const [newNote, setNewNote] = useState('');
  
  const notes = [
    {
      id: '1',
      date: '2024-01-15',
      author: 'Dr. Johnson',
      type: 'treatment',
      content: 'Patient responded excellently to Botox treatment. No adverse reactions noted. Scheduled follow-up in 2 weeks to assess results.',
      isPrivate: false
    },
    {
      id: '2',
      date: '2024-01-10',
      author: 'Sarah M.',
      type: 'admin',
      content: 'Patient requested to reschedule appointment due to work conflict. Very accommodating and understanding.',
      isPrivate: false
    },
    {
      id: '3',
      date: '2024-01-05',
      author: 'Dr. Smith',
      type: 'consultation',
      content: 'Initial consultation completed. Patient has realistic expectations and good understanding of treatment process. Cleared for Botox.',
      isPrivate: true
    }
  ];

  const getNoteTypeColor = (type: string) => {
    switch (type) {
      case 'treatment': return 'bg-green-100 text-green-800';
      case 'consultation': return 'bg-blue-100 text-blue-800';
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'follow-up': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold font-juv-heading" style={{ color: '#5C322B' }}>
          Patient Notes
        </h2>
      </div>

      {/* Add New Note */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Plus className="h-5 w-5" style={{ color: '#A55C52' }} />
            <span>Add New Note</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Enter patient note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            className="min-h-[100px]"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <select className="px-3 py-2 border rounded-lg text-sm">
                <option value="general">General Note</option>
                <option value="treatment">Treatment Note</option>
                <option value="consultation">Consultation Note</option>
                <option value="admin">Administrative Note</option>
                <option value="follow-up">Follow-up Note</option>
              </select>
              <label className="flex items-center space-x-2 text-sm">
                <input type="checkbox" />
                <span>Private Note</span>
              </label>
            </div>
            <Button 
              className="text-white" 
              style={{ backgroundColor: '#A55C52' }}
              disabled={!newNote.trim()}
            >
              Save Note
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notes History */}
      <div className="space-y-4">
        {notes.map((note) => (
          <Card key={note.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5" style={{ color: '#A55C52' }} />
                  <div>
                    <div className="flex items-center space-x-2">
                      <Badge className={getNoteTypeColor(note.type)}>
                        {note.type}
                      </Badge>
                      {note.isPrivate && (
                        <Badge variant="outline" className="text-red-600 border-red-600">
                          Private
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <User className="h-3 w-3" />
                    <span>{note.author}</span>
                  </div>
                  <div className="flex items-center space-x-1 mt-1">
                    <Calendar className="h-3 w-3" />
                    <span>{new Date(note.date).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              
              <p className="text-gray-700 leading-relaxed">
                {note.content}
              </p>
              
              <div className="mt-4 flex space-x-2">
                <Button variant="outline" size="sm">
                  Edit
                </Button>
                <Button variant="outline" size="sm">
                  Reply
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PatientNotes;
